import { ComponentProps, useRef, useState } from 'preact/compat';

import { getCurrencySymbol } from '@/components/Subscription/SubscriptionManager/helpers';
import { cn } from '@/shared/style';

import {
  CommentCreatedMessage,
  DonationMessage,
  FeedMessageWithMeta,
  MediaCreatedMessage,
  Message,
  SignUpMessage,
  SubscriptionMessage,
} from './types';

interface FeedItemProps {
  message: FeedMessageWithMeta;
}

interface CardProps<T extends Message> {
  message: T;
}

const DebugSection = <T,>({ value }: { value: T }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button type="button" onClick={() => setExpanded(!expanded)}>
        ...
      </button>
      {expanded && <pre className="overflow-x-auto font-mono text-xs">{JSON.stringify(value, null, 2)}</pre>}
    </div>
  );
};

const Card = ({ children, className, ...rest }: ComponentProps<'div'>) => {
  const backgroundColor = useRef<string>();
  if (!backgroundColor.current) {
    backgroundColor.current = `hsl(${Math.ceil(Math.random() * 360)}, 100%, 80%, 1)`;
  }
  return (
    <div
      className={cn('h-full rounded bg-slate-200 p-4 hover:shadow-md', className)}
      style={{ backgroundColor: backgroundColor.current }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {children}
    </div>
  );
};

const SignUpCard = ({ message }: CardProps<SignUpMessage>) => {
  return (
    <Card>
      <dl>
        <dt>Type</dt>
        <dd>{message.type}</dd>
        <dt>Time</dt>
        <dd>{new Date(message.triggeredAt).toLocaleString()}</dd>
        <dt>User</dt>
        <dd>
          <a href={`/u/${message.user.username}`}>{message.user.username}</a>
        </dd>
      </dl>
      <DebugSection value={message} />
    </Card>
  );
};

const SubscriptionCard = ({ message }: CardProps<SubscriptionMessage>) => {
  return (
    <Card>
      <dl>
        <dt>Type</dt>
        <dd>{message.type}</dd>
        <dt>Time</dt>
        <dd>{new Date(message.triggeredAt).toLocaleString()}</dd>
        <dt>User</dt>
        <dd>
          <a href={`/u/${message.user.username}`}>{message.user.username}</a>
        </dd>
      </dl>
      <DebugSection value={message} />
    </Card>
  );
};

const DonationCard = ({ message }: CardProps<DonationMessage>) => {
  return (
    <Card>
      <dl>
        <dt>Type</dt>
        <dd>{message.type}</dd>
        <dt>Time</dt>
        <dd>{new Date(message.triggeredAt).toLocaleString()}</dd>
        <dt>User</dt>
        <dd>
          <a href={`/u/${message.user.username}`}>{message.user.username}</a>
        </dd>
        <dt>Habitat</dt>
        <dd>
          <a href={`/h/${message.zoo.slug}/${message.habitat.slug}`}>
            {message.zoo.name} - {message.habitat.title}
          </a>
        </dd>
        <dt>Amount</dt>
        <dd>
          {getCurrencySymbol(message.currency)} {(message.amount / 100).toFixed(2)}
        </dd>
      </dl>
      <DebugSection value={message} />
    </Card>
  );
};

const CommentCreatedCard = ({ message }: CardProps<CommentCreatedMessage>) => {
  return (
    <Card>
      <dl>
        <dt>Type</dt>
        <dd>{message.type}</dd>
        <dt>Time</dt>
        <dd>{new Date(message.triggeredAt).toLocaleString()}</dd>
        <dt>User</dt>
        <dd>
          <a href={`/u/${message.user.username}`}>{message.user.username}</a>
        </dd>
        <dt>Habitat</dt>
        <dd>
          <a href={`/h/${message.zoo.slug}/${message.habitat.slug}`}>
            {message.zoo.name} - {message.habitat.title}
          </a>
        </dd>
        <dt>Chat ID</dt>
        <dd>{message.chatTimetoken}</dd>
      </dl>
      <DebugSection value={message} />
    </Card>
  );
};

const MediaCreatedCard = ({ message }: CardProps<MediaCreatedMessage>) => {
  return (
    <Card>
      <dl>
        <dt>Type</dt>
        <dd>{message.type}</dd>
        <dt>Time</dt>
        <dd>{new Date(message.triggeredAt).toLocaleString()}</dd>
        <dt>User</dt>
        <dd>
          <a href={`/u/${message.user.username}`}>{message.user.username}</a>
        </dd>
        <dt>Habitat</dt>
        <dd>
          <a href={`/h/${message.zoo.slug}/${message.habitat.slug}`}>
            {message.zoo.name} - {message.habitat.title}
          </a>
        </dd>
        <dt>Media Type</dt>
        <dd>{message.mediaType}</dd>
        <dt>Media ID</dt>
        <dd>
          <a href={`/h/${message.zoo.slug}/${message.habitat.slug}?card=album&id=${message.mediaId}`}>{message.mediaId}</a>
        </dd>
      </dl>
      <DebugSection value={message} />
    </Card>
  );
};

const FeedItem = ({ message: messageWithMeta }: FeedItemProps) => {
  const { message } = messageWithMeta;
  return message.type === 'sign-up' ? (
    <SignUpCard message={message} />
  ) : message.type === 'subscription' ? (
    <SubscriptionCard message={message} />
  ) : message.type === 'comment-created' ? (
    <CommentCreatedCard message={message} />
  ) : message.type === 'media-created' ? (
    <MediaCreatedCard message={message} />
  ) : message.type === 'donation' ? (
    <DonationCard message={message} />
  ) : null;
};

export default FeedItem;
